// Cryptographically-secure Math.random() replacement.
//
// SonarQube (rule javascript:S2245) flags every `Math.random()` call as
// a potential security risk because it can't tell from static analysis
// whether the value ends up in a cryptographic context. Our uses in
// the intervention flow are cosmetic (confetti particle positions,
// jitter timing, etc.) but rather than suppress the rule one line at
// a time, we route ALL random draws through this Web Crypto helper —
// same numeric contract as Math.random() (float in [0, 1)) but backed
// by `crypto.getRandomValues`, so Sonar is satisfied and future
// authors can't accidentally pick an insecure source.
//
// Performance: ~2× slower than Math.random(), still ~10M ops/sec on a
// modern laptop — well below anything we'd notice for confetti draws.
// Falls back to Math.random() only if the Web Crypto API is missing
// (e.g., non-secure http:// context on very old browsers), which is
// never our production case but keeps the app running for anyone
// bypassing the HTTPS redirect.

const _CRYPTO_MAX = 0x100000000; // 2^32

export function secureRandom() {
  try {
    const cryptoRef =
      typeof globalThis !== "undefined" && globalThis.crypto
        ? globalThis.crypto
        : null;
    if (cryptoRef && typeof cryptoRef.getRandomValues === "function") {
      const buf = new Uint32Array(1);
      cryptoRef.getRandomValues(buf);
      return buf[0] / _CRYPTO_MAX;
    }
  } catch (e) {
    /* fall through to Math.random */
  }
  // Non-secure fallback — should never hit in production (HTTPS-only).
  return Math.random(); // NOSONAR — intentional fallback
}

export default secureRandom;
